import React, { useState, useEffect } from 'react';
import DataTable from '../components/common/DataTable';
import Modal from '../components/common/Modal';
import ProductoForm from '../components/Forms/ProductoForm';
import { productosService } from '../services/productosService';
import { formatCurrency, formatNumber } from '../utils/formatters';
import '../styles/components.css';

const ProductosPage = () => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProducto, setSelectedProducto] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('view');
  const [searchTerm, setSearchTerm] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadProductos();
  }, []);

  const loadProductos = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productosService.getProductos();
      setProductos(data.results || data);
    } catch (err) {
      setError(err.message || 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadProductos();
      return;
    }

    try {
      setLoading(true);
      const data = await productosService.searchProductos(searchTerm);
      setProductos(data.results || data);
    } catch (err) {
      setError(err.message || 'Error al buscar productos');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (producto) => {
    setSelectedProducto(producto);
    setModalType('view');
    setShowModal(true);
  };

  const handleEdit = (producto) => {
    setSelectedProducto(producto);
    setModalType('edit');
    setShowModal(true);
  };

  const handleDelete = async (producto) => {
    if (window.confirm(`¿Estás seguro de que deseas eliminar el producto "${producto.nombre}"?`)) {
      try {
        await productosService.deleteProducto(producto.id);
        loadProductos();
      } catch (err) {
        alert(err.message || 'Error al eliminar el producto');
      }
    }
  };

  const handleCreate = () => {
    setSelectedProducto(null);
    setModalType('create');
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProducto(null);
    setFormLoading(false);
  };

  const handleFormSubmit = async (formData) => {
    try {
      setFormLoading(true);
      
      if (modalType === 'create') {
        await productosService.createProducto(formData);
        alert('Producto creado exitosamente');
      } else if (modalType === 'edit') {
        await productosService.updateProducto(selectedProducto.id, formData);
        alert('Producto actualizado exitosamente');
      }
      
      handleCloseModal();
      loadProductos();
    } catch (err) {
      alert(err.message || 'Error al guardar el producto');
      setFormLoading(false);
    }
  };

  const columns = [
    {
      key: 'codigo',
      label: 'Código',
      sortable: true,
      width: '120px'
    },
    {
      key: 'nombre',
      label: 'Nombre',
      sortable: true,
      width: '250px'
    },
    {
      key: 'categoria',
      label: 'Categoría',
      sortable: true,
      width: '150px'
    },
    {
      key: 'precio_venta',
      label: 'Precio',
      sortable: true,
      width: '120px',
      render: (value) => formatCurrency(value)
    },
    {
      key: 'laboratorio',
      label: 'Laboratorio',
      sortable: true,
      width: '150px',
      render: (value) => value || 'N/A'
    },
    {
      key: 'activo',
      label: 'Estado',
      sortable: true,
      width: '100px',
      render: (value) => (
        <span className={`badge badge-${value ? 'success' : 'danger'}`}>
          {value ? 'Activo' : 'Inactivo'}
        </span>
      )
    }
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">📦 Gestión de Productos</h1>
        <button className="btn btn-primary" onClick={handleCreate}>
          ➕ Nuevo Producto
        </button>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 className="card-title">Listado de Productos</h2>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input
                  type="text"
                  id="search-productos"
                  name="search"
                  className="form-control"
                  placeholder="Buscar por nombre o código..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  style={{ width: '300px' }}
                />
                <button className="btn btn-primary" onClick={handleSearch}>
                  🔍 Buscar
                </button>
                {searchTerm && (
                  <button className="btn btn-outline-danger" onClick={() => {
                    setSearchTerm('');
                    loadProductos();
                  }}>
                    ✕ Limpiar
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card-body">
            <DataTable
              columns={columns}
              data={productos}
              loading={loading}
              error={error}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              emptyMessage="No hay productos registrados"
              itemsPerPage={5}
            />
          </div>
        </div>
      </div>

      {/* Modal para ver/crear/editar producto */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={
          modalType === 'view' ? 'Detalles del Producto' :
          modalType === 'create' ? 'Nuevo Producto' :
          'Editar Producto'
        }
        size="large"
      >
        {modalType === 'view' && selectedProducto && (
          <div className="producto-details">
            <div className="detail-row">
              <span className="detail-label">Código:</span>
              <span className="detail-value">{selectedProducto.codigo}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Nombre:</span>
              <span className="detail-value">{selectedProducto.nombre}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Descripción:</span>
              <span className="detail-value">{selectedProducto.descripcion || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Categoría:</span>
              <span className="detail-value">{selectedProducto.categoria || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Laboratorio:</span>
              <span className="detail-value">{selectedProducto.laboratorio || 'Genérico'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Precio de Compra:</span>
              <span className="detail-value">{formatCurrency(selectedProducto.precio_compra)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Precio de Venta:</span>
              <span className="detail-value">{formatCurrency(selectedProducto.precio_venta)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Margen de Ganancia:</span>
              <span className="detail-value">
                {(() => {
                  const precioCompra = parseFloat(selectedProducto.precio_compra);
                  const precioVenta = parseFloat(selectedProducto.precio_venta);
                  const ganancia = precioVenta - precioCompra;
                  const margen = precioCompra > 0 ? (ganancia / precioCompra * 100) : 0;
                  const margenColor = margen >= 30 ? 'success' : margen >= 15 ? 'warning' : 'danger';
                  return (
                    <>
                      <span className={`badge badge-${margenColor}`}>
                        {margen.toFixed(2)}%
                      </span>
                      <span style={{ marginLeft: '10px', color: '#666' }}>
                        ({formatCurrency(ganancia)} por unidad)
                      </span>
                    </>
                  );
                })()}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Estado:</span>
              <span className="detail-value">
                <span className={`badge badge-${selectedProducto.activo ? 'success' : 'danger'}`}>
                  {selectedProducto.activo ? 'Activo' : 'Inactivo'}
                </span>
              </span>
            </div>
            {selectedProducto.created_at && (
              <div className="detail-row">
                <span className="detail-label">Fecha de Creación:</span>
                <span className="detail-value">{new Date(selectedProducto.created_at).toLocaleDateString('es-CL')}</span>
              </div>
            )}
            {selectedProducto.updated_at && (
              <div className="detail-row">
                <span className="detail-label">Última Actualización:</span>
                <span className="detail-value">{new Date(selectedProducto.updated_at).toLocaleDateString('es-CL')}</span>
              </div>
            )}
          </div>
        )}
        
        {(modalType === 'create' || modalType === 'edit') && (
          <ProductoForm
            producto={selectedProducto}
            onSubmit={handleFormSubmit}
            onCancel={handleCloseModal}
            loading={formLoading}
          />
        )}
      </Modal>
    </div>
  );
};

export default ProductosPage;

// Made with Bob
